import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0201 from "../../course-02/lesson-01/lesson";
import lesson0506 from "../../course-05/lesson-06/lesson";
import lesson0512 from "../../course-05/lesson-12/lesson";

const linkLesson0201 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0201.id);
const linkLesson0512 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0512.id);
const linkLesson0506 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0506.id);

/* eslint-disable */
const theory = `<h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson, you will learn about different types of noise sources that contribute to the overall background noise level in a closed space, and possible ways to avoid or mitigate them correctly in order to achieve a comfortable acoustic environment. You will be introduced to the concept of noise rating using the Noise Rating (NR) curves. Information will be given on the criteria for background noise assessment in different types of spaces through appropriate Noise Rating values and the A-weighted noise levels.<br class=newline>
<h3 class=sectionHead>Introduction</h3>
<p>Let us start at the beginning of a building project. At the early stages of design, a set of target values of the background noise level should be proposed. These values are chosen based on the acoustic sensitivity of the room which is closely connected with the intended use(s) of the room. Even in acoustically non-sensitive rooms, the maximum permissible background noise level still needs to be given, in order to avoid excessively loud operating or working conditions. In other words, the stipulated criteria will depend not only on the intended use of the space, but also on its surroundings that might need some kind of noise protection due to the presence of the noise source.<br class=newline>
<p>To give an example: a bedroom is a calm space used mainly for rest and sleep. Therefore, it should be a very quiet environment with a very low-level or even inaudible background noise if it is to suit the needs of the occupants. On the other hand, a technical room could be handling high levels of noise generated by service equipment. In both cases, the maximum permissible noise level will be stipulated, in order to reduce the cost and complexity of the noise protection measures that need to be implemented to protect one room from the noise coming from the other.
<h3 class=sectionHead>What are common noise sources contributing to background noise?</h3>
<p>The following summary lists the main types of noise sources involved in creating continuous or intermittent background noise in a closed space:
<ul class=itemize1>
  <li class=itemize>Building service equipment such as air handling units (AHU), pumps, fan coil units and other HVAC equipment are potentially loud noise sources that need to be located in dedicated technical rooms positioned sufficiently far away from noise-sensitive spaces. On the other hand, regenerated noise as the consequence of high air velocity through ducts and ventilation terminals also contributes to the overall background noise in a space. These elements need to be properly designed in the early stages of a project.
  <li class=itemize>Audiovisual, lighting and other electrical equipment such as video projectors, lighting, dimmers, electrical racks, computers and networking hardware also contribute to background noise, especially when they are equipped with small-diameter, high-RPM integrated cooling fans. In very sensitive spaces such as concert halls or cinemas, it is recommended to choose equipment with passive cooling only, i.e. with no cooling fans, or to place equipment with small cooling fans into a separate room.
  <li class=itemize>Structure-borne noise comes from vibrations propagating through the structure, and these vibrations can be radiated as noise virtually anywhere in the building. Therefore, structure-borne noise can also contribute to the overall background noise level. The sources of structure-borne noise can be continuous sources such as pumps, HVAC units, etc., or intermittent sources such as chillers, washing machines, etc., located in the surrounding spaces.
  <li class=itemize>Airborne noise can propagate into a room from surrounding spaces such as technical rooms, common spaces in the building or other dwellings. Airborne noise can penetrate in to a room from outside of the building. The most common sources of outdoor noise are road traffic, air traffic, railway traffic, industry, outdoor activities, etc.
</ul>
<h3 class=sectionHead>Why are Noise Rating values and A-weighted sound pressure levels used to quantify background noise?</h3>
<h4 class=subsectionHead>What are the Noise Rating curves?</h4>
<p>To assess and quantify background noise levels in buildings by means of a single number value, taking into account the frequency content of the noise, the ISO developed the so-called Noise Rating (NR) method (ISO/R1996:1971). Noise Rating values come from a set of Noise Rating curves ranging from NR0 to NR130. These curves take into account the frequency-dependent sensitivity of the human hearing system. As explained in the Psychoacoustics course <span class=cmti-10><a href="${linkLesson0201}" target="_blank">Lesson 2.1</a></span>, the human hearing system is much more sensitive at middle and high frequencies than it is at low frequencies. Therefore, the acoustic (dis)comfort in a space will highly depend on the spectral content of the background noise.<br class=newline>
<p>Each NR curve defines the maximum noise level in each octave band that must not be exceeded for the corresponding Noise Rating (see <a href="#" data-img-alt="Figure 1. Comparison of NC curves and NR curves." data-img-src=${config.ASSETS_URL}/05/29/theory/figure-1.svg>Figure 1</a> below).<br class=newline>
<p>Note: Noise Rating (NR) is commonly used in Europe, whereas Noise Criterion (NC) is more common in the USA. The difference between the two is that the NR rating system is less stringent in terms of maximum noise levels at low frequencies, but more stringent at high frequencies.<br class=newline>
<p>The Noise Criterion can be linked with the Noise Rating as follows:
<table class=equation>
  <tr>
    <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <mi mathvariant=italic>NC</mi>
        <mo class=MathClass-rel>=</mo>
        <mi mathvariant=italic>NR</mi>
        <mo class=MathClass-bin>-</mo>
        <mn>2</mn>
        <mi mathvariant=italic>&nbsp;dB</mi>
      </math>
    <td class=eq-no>(1)
</table>
<figure class=figure>
  <p>
    <img alt="Figure 1. Comparison of NC curves and NR curves." src=${config.ASSETS_URL}/05/29/theory/figure-1.svg>
    <figcaption class=caption><span class=id>Figure 1. Comparison of NC curves and NR curves.</span></figcaption>
</figure>
<p>The spectrum of background noise measured in a space rarely follows the shape of NR curves across the octave bands of interest. As an alternative or a complementary criterion, the maximum value of the A-weighted background noise level expressed in dB(A) can also be proposed as a general target value for a room. As explained in <span class=cmti-10><a href="${linkLesson0512}" target="_blank">Lesson 5.12</a></span>, this value is obtained by applying the A-weighting filter to measured or recorded noise. The A-weighting was derived from the frequency-dependent sensitivity of the human hearing system at low to normal loudness levels. As such, it puts the most weight on the spectral content in the frequency range between 800 Hz and 5 kHz. Less weight is put on high-frequency content, and very little on low-frequency content. Therefore, the overall A-weighted level is the weighted sum of the octave or 1/3-octave band levels, whereas the NR value depends on the “loudest” octave band. In practice, the following rule-of-thumb can be used to link the A-weighted level with the corresponding Noise Rating:
<table class=equation>
  <tr>
    <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi mathvariant=italic>pA</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <mi mathvariant=italic>NR</mi>
        <mo class=MathClass-bin>+</mo>
        <mn>5</mn>
        <mi mathvariant=italic>&nbsp;dB</mi>
      </math>
    <td class=eq-no>(2)
</table>
<p>How is Noise Rating calculated from measured data?
<ol class=enumerate1>
  <li id=x1-5006x1 class=enumerate>Plot the measured octave band levels (no weighting applied) against the NR curves
  <li id=x1-5008x2 class=enumerate>Find the highest NR curve that is reached by one of these values
  <li id=x1-5010x3 class=enumerate>The NR curve found in the previous step defines the single-number Noise Rating for the investigated noise
</ol>
<p>
  The following example shows how the Noise Rating is determined for the noise generated by a ventilation Air Handling Unit. The unweighted octave-band spectrum of that noise is plotted against the NR curves. The highest NR curve is reached by the noise level in the octave band at 250 Hz, and it is the NR39 curve. The corresponding Noise Rating is then NR39.
  <figure class=figure>
<p><img alt="Figure 2. Noise level measured in the receiving room - after 5 meters of lined duct and no silencers between the AHU and the receiving room." src=${config.ASSETS_URL}/05/29/theory/figure-2.svg><figcaption class=caption><span class=id>Figure 2. Noise level measured in the receiving room - after 5 meters of lined duct and no silencers between the AHU and the receiving room.</span></figcaption></figure>
<h4 class=subsectionHead>Background noise levels and the corresponding perception of indoor environments</h4>
<p>
  The following chart displays how indoor environments are perceived depending on the amount of background noise quantified with the associated Noise Rating and the A-weighted noise level expressed in dB(A). In addition, the chart also indicates suitable target levels of background noise for different types of rooms, depending on their function and purpose.
  <figure class=figure>
<p><img src=${config.ASSETS_URL}/05/29/theory/figure-3.svg></figure>
<h4 class=subsectionHead>Steps to calculate background noise levels in a room</h4>
<p>The <strong><span class=cmbx-10>sound pressure level</span></strong> in a space is calculated from the <strong><span class=cmbx-10>sound power levels</span></strong> of noise-generating equipment. The noise is transmitted into ducts, attenuated by silencers and elbows, regenerated by silencers, volume dampers and fire dampers, terminals, etc.<br class=newline>
<p>To calculate the noise level in a room, it is necessary to consider the energy propagation, which means that the sound power level needs to be found before and after each element of the ventilation system at each frequency octave band.<br class=newline>
<p>
  The sound power level, noted SWL or
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi mathvariant=italic>Lw</mi>
  </math>
  , is then transformed into the sound pressure level, noted SPL or
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi mathvariant=italic>Lp</mi>
  </math>
  . The level of direct sound depends on the distance from the noise source to the receiver in the room. The reverberant sound does not depend on the distance from the source.<br class=newline>
<p>
  The total noise level measured in the space is the logarithmic sum of the direct noise level
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi mathvariant=italic>Lp</mi>
    <mo class=MathClass-punc>,</mo>
    <mi mathvariant=italic>dir</mi>
  </math>
  and reverberant noise level
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi mathvariant=italic>Lp</mi>
    <mo class=MathClass-punc>,</mo>
    <mi mathvariant=italic>rev</mi>
  </math>
  , and is written as:
<table class=equation>
  <tr>
    <td>
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>TOTAL</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>dir</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>⊕</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>rev</mi>
          </mrow>
        </msub>
      </math>
    <td class=eq-no>(3)
</table>
<p>These two sound field components have been discussed in <span class=cmti-10><a href="${linkLesson0506}" target="_blank">Lesson 5.6</a></span>. The following simplified equations are used to calculate the noise levels in a room:
<table class=equation>
  <tr>
    <td style="font-size: 15px;">
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>dir</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>w</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>−</mo>
        <mn>20</mn>
        <mi mathvariant=italic>log</mi>
        <mo class=MathClass-open>(</mo>
        <mi>r</mi>
        <mo class=MathClass-close>)</mo>
        <mo class=MathClass-bin>−</mo>
        <mn>11</mn>
        <mo class=MathClass-bin>+</mo>
        <mn>10</mn>
        <mi mathvariant=italic>log</mi>
        <mo class=MathClass-open>(</mo>
        <mi>Q</mi>
        <mo class=MathClass-close>)</mo>
      </math>
    <td class=eq-no>(4)
</table>
<table class=equation>
  <tr>
    <td style="font-size: 15px;">
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>p</mi>
            <mo class=MathClass-punc>,</mo>
            <mi mathvariant=italic>rev</mi>
          </mrow>
        </msub>
        <mo class=MathClass-rel>=</mo>
        <msub>
          <mrow>
            <mi>L</mi>
          </mrow>
          <mrow>
            <mi>w</mi>
          </mrow>
        </msub>
        <mo class=MathClass-bin>−</mo>
        <mn>10</mn>
        <mi mathvariant=italic>log</mi>
        <mo class=MathClass-open>(</mo>
        <mi>A</mi>
        <mo class=MathClass-close>)</mo>
        <mo class=MathClass-bin>+</mo>
        <mn>6</mn>
        <mo class=MathClass-bin>+</mo>
        <mn>10</mn>
        <mi mathvariant=italic>log</mi>
        <mo class=MathClass-open>(</mo>
        <mi>N</mi>
        <mo class=MathClass-close>)</mo>
      </math>
    <td class=eq-no>(5)
</table>
<p>
  In the equations above,
  <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <mi mathvariant=italic>Lw</mi>
  </math>
  represents the sound power level of the source in the room (in dB), r is the distance to the source (in m), and Q is the directivity factor of the source depending on its position in the room, assuming omnidirectional sound emission. Specifically, Q = 1 in a free field, Q = 2 on a flat reflective surface, Q = 4 at the junction of two perpendicular surfaces (e.g. two walls), and Q = 8 in a corner of the room. The total amount of absorption present in the room is expressed with A (also known as equivalent absorption area and expressed in metric Sabines), and N is the total number of equivalent noise sources in the room.<br class=newline>
<p><span class=underline>Remarks</span>:
<ol class=enumerate1>
  <li id=x1-7005x1 class=enumerate>In a room, the level of direct sound will be higher when the receiver is close to the source.
  <li id=x1-7007x2 class=enumerate>
    Reverberant sound will quickly become the predominant component of the background noise as the receiver moves away from the source. In other words, for a larger distance r between source and receiver positions:
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>rev</mi>
    </math>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-rel>></mo>
      <mo class=MathClass-rel>></mo>
    </math>
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>dir</mi>
    </math>
    , which implies
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
        <mrow>
          <mi>L</mi>
        </mrow>
        <mrow>
          <mi>p</mi>
          <mo class=MathClass-punc>,</mo>
          <mi mathvariant=italic>TOTAL</mi>
        </mrow>
      </msub>
    </math>
    =
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lp</mi>
      <mo class=MathClass-punc>,</mo>
      <mi mathvariant=italic>rev</mi>
    </math>
    .
  <li id=x1-7009x3 class=enumerate>
    In equation (5), we can see that the number of terminal units contributes significantly to the overall background noise level in a room. Therefore, the
    <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Lw</mi>
    </math>
    of a terminal, as well as the total number of terminal units in the room need to be taken into account.
</ol>
<h3 class=sectionHead>Common elements of the ventilation network</h3>
<p>Let us now look into the effect of some common elements of the ventilation network.
<h4 class=subsectionHead>Ducts</h4>
<p>
  Noise can get into ducts that are directly or indirectly connected to noisy equipment. Therefore, to avoid crosstalk between spaces, it is important to acoustically separate any sensitive duct passing through a noisy room, and any noisy duct going through a sensitive room. We will see another example in the <strong><span class=cmbx-10>sound attenuator</span></strong> section below.<br class=newline><br class=newline>The sound is attenuated with distance travelled inside a duct. The attenuation ranges from about 0.1 dB/m to 1 dB/m for unlined rectangular sheet metal ducts, and from about 0.03 dB/m to 0.33 dB/m for unlined circular ducts (see tables below). Moreover, the attenuation will surely be higher for an internally lined duct compared to an unlined duct, especially at middle and high frequencies. Furthermore, a rectangular duct will provide greater attenuation than a circular duct, i.e. by up to a few dB (depending on the duct length). <strong><span class=cmbx-10>Bends</span></strong> in ducts also help attenuate middle- and high-frequency noise. However, the <strong><span class=cmbx-10>free area</span></strong> needs to be properly dimensioned in order not to regenerate noise outside the duct, i.e. in the room the duct is running through.
  <figure class=figure>
    <figcaption class=caption><span class=id>Table 1. Sound Attenuation in Unlined Rectangular Metal Ducts, from [1].</span></figcaption>
    <img alt="Table 1. Sound Attenuation in Unlined Rectangular Metal Ducts, from [1]." src=${config.ASSETS_URL}/05/29/theory/table-1.svg>
  </figure>
  <figure class=figure>
    <figcaption class=caption><span class=id>Table 2. Sound Attenuation in Straight Round Ducts, from [1].</span></figcaption>
    <img alt="Table 2. Sound Attenuation in Straight Round Ducts, from [1]." src=${config.ASSETS_URL}/05/29/theory/table-2.svg>
  </figure>
<h4 class=subsectionHead>Silencers (also known as Attenuators)</h4>
<p>Silencers are devices in a duct network in which air flows around absorbing baffles, which causes the sound waves carried along a duct to be attenuated. This attenuation is called the <strong><span class=cmbx-10>Insertion Loss (IL)</span></strong> of a silencer.<br class=newline>
<p>There are two types of silencers, i.e rectangular and circular ones:
<ul class=itemize1>
  <li class=itemize>Rectangular silencers can vary in length, the number of rectangular baffles, the thickness of individual baffles and the spacing between them.
  <li class=itemize>Circular silencers can only vary in length and the thickness of the circular baffle.
</ul>
<p><span class=underline>Remark:</span> Rectangular silencers display higher insertion losses than circular silencers, especially at low frequencies. Therefore, high attenuation is easier to achieve using a rectangular silencer.<br class=newline>
<p>It is also important to look at the noise regenerated by silencers, which will depend on the <strong><span class=cmbx-10>air speed</span></strong> across these elements. Acoustically, it is recommended to keep the air speed below 6 m/s on the output of the technical room, and below 3 m/s when close to an acoustically sensitive space. Once again, rectangular silencers will have lower regenerated noise levels than circular ones, for the same free area.<br class=newline>
<p>A silencer can also help attenuate noise between spaces when the same duct supplies several adjacent spaces. This type of silencer is called a <strong><span class=cmbx-10>crosstalk</span></strong> attenuator. It needs to be placed at the separating wall, floor or ceiling to prevent the noise produced in one room from entering the duct after the silencer, thus “short-cutting” its performance. Note that a crosstalk attenuator attenuates sound in a broad frequency range.
<h4 class=subsectionHead>Plenums</h4>
<p>A plenum is basically a chamber with considerable volume that functions as a part of the duct network. The large volume enables the required airflow to be achieved at <strong><span class=cmbx-10>low air speeds</span></strong>, thus facilitating a significant reduction of noise levels generated by a duct. The use of plenums is usually recommended in noise-sensitive spaces which require very low noise rating of <strong><span class=cmbx-10>NR20 or </span><span class=cmbx-10>less</span></strong>. A plenum solves this problem by reducing the air speed through the diffusers to less than 1 m/s. Moreover, a plenum allows natural distribution of air through each diffuser, without the need for additional elements such as volume dampers (see below), thus eliminating the problem of regenerated noise. In addition, plenums can be made of plasterboard which is more easily integrated into the overall design and is fireproof, thus avoiding regenerated noise coming from fire dampers (see below) located close to the terminal.<br class=newline>As large, closed volumes, plenums can be found under the floor of an office space or under the audience area in a concert hall.
<h4 class=subsectionHead>Volume dampers</h4>
<p>A volume damper is often used to split the airflow in the duct network. However, these types of elements tend to regenerate noise as they decrease the free area of the duct, thus locally increasing the air velocity and creating turbulent airflow. It is important to position them as far away from a terminal as possible and (if possible) before a silencer, in order not to regenerate more noise too close to a room.<br class=newline>
<p>In very sensitive rooms (with target noise rating of NR25 or less), it is recommended to avoid placing the volume dampers less than 6 m from the room, as the regenerated noise would be very difficult to attenuate. A <strong><span class=cmbx-10>natural distribution</span></strong> of air between rooms with silencers put in-between is recommended in these cases.
<h4 class=subsectionHead>Fire dampers</h4>
<p>Fire dampers are usually needed at a fire junction in the building. As these elements require an extra layer to be integrated from within into the existing duct, the free area of the duct is reduced locally. This leads to a local increase of air speed and, consequently, to regenerated noise.<br class=newline>
<p>Acoustically, it is recommended to place fire dampers before a silencer that will attenuate the regenerated noise before it reaches the terminal. The part of the ventilation network which needs to be fireproofed can then be encased in a <strong><span class=cmbx-10>fireproof lining</span></strong>, which can also encompass the silencer. However, such a configuration takes up space and requires some coordination with the M&E and fire engineers.
<h4 class=subsectionHead>Terminals</h4>
<p>Terminals need to have enough free area for the air to go through at a low enough air speed, in order to <strong><span class=cmbx-10>avoid turbulent air flow</span></strong> which is audible in the room as high-frequency noise. For very sensitive spaces with the target noise rating of NR25 or lower, the air speed at the terminal needs to be lower than 2 m/s, which requires larger <strong><span class=cmbx-10>terminal surfaces</span></strong> and/or larger <strong><span class=cmbx-10>opening </span><span class=cmbx-10>areas</span></strong>. In such spaces it is not allowed to mount volume dampers at the terminal, as the regenerated noise will be impossible to attenuate in the room.
<h3 class=sectionHead>Key guidelines to avoiding noise issues in building design</h3>
<p>In order to achieve the noise targets explained above, recommendations for ventilation design from the acoustical point of view are given below.<br class=newline>
<p><span class=underline><strong><span class=cmbx-10>In technical rooms:</span></strong></span>
<ul class=itemize1>
  <li class=itemize>Place noisy equipment as far away from noise-sensitive rooms as possible
  <li class=itemize>Select appropriate equipment with low noise levels (according to the NR/dB(A) target values)
  <li class=itemize>Install appropriately dimensioned duct silencers. One should be installed in the technical room to attenuate the equipment fan noise. For stringent NR targets, a second silencer should be installed closer to the noise-sensitive room to provide further noise attenuation. In some cases, flexible acoustic ducts can also be used near the terminals.
  <li class=itemize>
    If the target noise level inside the technical room is not achieved:
    <ul class=itemize2>
      <li class=itemize>Enclose the noisy equipment with an acoustic enclosure, or
      <li class=itemize>Duplicate the number of items to decrease fan speed and/or volume flow in ducts and through terminals.
    </ul>
</ul>
<p><span class=underline><strong><span class=cmbx-10>In the building / in the room:</span></strong></span>
<ul class=itemize1>
  <li class=itemize>Limit the airspeed in ducts, through silencers and at the terminals by sizing the free area of these elements according to the required volume flow, if low noise level targets need to be achieved (less than NR35 and 40 dB(A)). The table below gives the air speed limits to achieve particular low noise targets.
  <li class=itemize>
    If the targeted noise level inside the room is not achieved because of too high air flow speeds:
    <ul class=itemize2>
      <li class=itemize>Duplicate the number of items to decrease the volume flow (and air speeds) through ducts and terminals.
      <li class=itemize>Enlarge the ducts and terminals to ensure that the appropriate air speeds listed below are not exceeded.
      <li class=itemize>Avoid regenerating noise elements such as volume dampers, fire dampers, etc. close to the terminals. For noise-critical spaces, these elements should be located more than 3 m away from a terminal and followed by a silencer.
    </ul>
  <li class=itemize>
    If the targeted noise level inside the room is not achieved because of high level of noise generated by the equipment inside the room:
    <ul class=itemize2>
      <li class=itemize>Place noisy items (such as fan coil units, motorized lighting, video projectors, etc.) outside a sensitive space, and/or
      <li class=itemize>Enclose any noisy item that cannot be placed outside a sensitive space (such as a video or cinema projector, motorized lighting, etc.) with an acoustic casing or a plasterboard box with mineral wool. The box must include an air inlet/outlet with a silencer, if the equipment uses forced cooling by means of a cooling fan.
    </ul>
</ul>
<p>To achieve the air speeds listed in the table below, the free area needs to be dimensioned according to the required volume air flow, as follows:
<table class=equation>
  <tr>
    <td style="font-size: 11px">
      <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
        <mi mathvariant=italic>Free</mi>
        <mi mathvariant=italic>area</mi>
        <mo class=MathClass-open>[</mo>
        <msup>
          <mrow>
            <mi>m</mi>
          </mrow>
          <mrow>
            <mn>2</mn>
          </mrow>
        </msup>
        <mo class=MathClass-close>]</mo>
        <mo class=MathClass-rel>=</mo>
        <mfrac>
          <mrow>
            <mi>V</mi>
            <mi mathvariant=italic>olume</mi>
            <mi mathvariant=italic>airflow</mi>
            <mo class=MathClass-open>[</mo>
            <msup>
              <mrow>
                <mi>m</mi>
              </mrow>
              <mrow>
                <mn>3</mn>
              </mrow>
            </msup>
            <mo class=MathClass-bin>∕</mo>
            <mi>h</mi>
            <mo class=MathClass-close>]</mo>
          </mrow>
          <mrow>
            <mi mathvariant=italic>Airspeed</mi>
            <mo class=MathClass-open>[</mo>
            <mi>m</mi>
            <mo class=MathClass-bin>∕</mo>
            <mi>s</mi>
            <mo class=MathClass-close>]</mo>
          </mrow>
        </mfrac>
        <mfrac>
          <mrow>
            <mn>1</mn>
          </mrow>
          <mrow>
            <mn>3600</mn>
          </mrow>
        </mfrac>
      </math>
    <td class=eq-no>(6)
</table>
<figure class=figure>
  <p>
    <figcaption class=caption><span class=id>Table 3. Air speed limits (m/s) at different distances from the room terminal according to the Noise Rating targeted.</span></figcaption>
    <img alt="Table 3. Air speed limits (m/s) at different distances from the room terminal according to the Noise Rating targeted." src=${config.ASSETS_URL}/05/29/theory/table-3.svg>
</figure>
<p>
<h3 class=likesectionHead>Glossary</h3>
<div class=supertabular>
  <dl>
    <dt>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lp</mi>
      </math>
    </dt>
    <dd>sound pressure level</dd>
    <dt>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
        <mi mathvariant=italic>Lw</mi>
      </math>
    </dt>
    <dd>sound power level</dd>
    <dt>
        <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
          <mi mathvariant=italic>Lp</mi>
          <mo class=MathClass-punc>,</mo>
          <mi mathvariant=italic>dir</mi>
        </math>
    </dt>
    <dd>direct noise level</dd>
    <dt>
        <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
          <mi mathvariant=italic>Lp</mi>
          <mo class=MathClass-punc>,</mo>
          <mi mathvariant=italic>rev</mi>
        </math>
    </dt>
    <dd>reverberant noise level</dd>
    <dt>A</dt><dd>equivalent absorption area</dd>
    <dt>AHU</dt><dd>air handling units</dd>
    <dt>HVAC</dt><dd>heating, ventilation and air-conditioning</dd>
    <dt>IL</dt><dd>Insertion Loss</dd>
    <dt>N</dt><dd>number of noise sources in the room</dd>
    <dt>NC</dt><dd>Noise Criterion</dd>
    <dt>NR</dt><dd>Noise Rating</dd>
    <dt>Q</dt><dd>directivity factor of the source</dd>
    <dt>r</dt><dd>distance to the source</dd>
  </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt id=X0-ASHRAE2011 class=thebibliography>[1]
  <dd id=bib-1 class=thebibliography>
    <p><span class=cmti-10>ASHRAE Handbook - HVAC Applications. Chapter 48 : Noise and </span><span class=cmti-10>Vibration Control</span>. American Society of Heating, Refrigerating and Air-Conditioning Engineers, Inc., 2011.
</dl>`;
/* eslint-enable */

export default theory;
